import { createStore } from 'vuex'
import axios from "@/axios";
import Usuario from '@/types/usuario';
import VuexPersistence from "vuex-persist"
import { addHours, isBefore, parseISO } from 'date-fns';
import helpers from '@/helpers';
import router from '@/router';
import { Motorista } from '@/types/motorista';
import { Placa } from '@/types/placa';
import { Acesso } from '@/types/acessos';
import { AxiosRequestConfig } from 'axios';
import {
  DisponibilidadeMes,
  HorarioDisponivel,
  HorariosDisponiveisResposta,
  ParametrosDisponibilidadeMes,
  ParametrosHorariosDisponiveis,
  StatusDiaJanela,
} from '@/types/janela_agendamento';
import { FiltrosNotasPallet, NotasPalletResposta } from '@/types/nota_pallet';


interface ILogin {
  usuario: string;
  password: string;
  cia_ukey: string;
  device_name: string;
}

interface TSelectObject {
  value: any;
  name: string;
}

type TArray = TSelectObject[];

type TArrayExpiracao = {
  expiraEm: Date;
  valores: TArray;
};

interface DataNewAgendamento {
  tipo_carga: string;
  placa: string;
  transportadora: string;
  veiculo: string;
  motorista: string;
  setor: string;
  celular: string;
  data: string;
  periodo: number | string;
  horario: string;
  operacao: number | string;
}

interface DataSolicitaAcesso {
  cnpj: string;
  email: string;
  celular: string;
  observacoes: string;
  razao_social: string;
}


interface IFiltrosMeusAgendamentos {
  data: string;
  setor_ukey: string;
  periodo: string;
  tipo_carga: string;
  a29_ukey: string;
  finalizado: number;
  operacao: number;
}

type DDI = { nome: string; ddi: string; ukey: string; }

const semLoaderGlobal = { customLoader: true } as AxiosRequestConfig & { customLoader: boolean };

export default createStore({
  state: {
    auth_token: "",
    user: {} as Usuario,
    arr: {} as Record<string, TArrayExpiracao>,
    isSavingAgendamento: false,
    meus_agendamentos: [],
    meus_agendamentos_paginacao: { current_page: 1, last_page: 1, total: 0 },
    meus_agendamentos_filtros: {} as IFiltrosMeusAgendamentos,
    isLoadingSolicitaAcesso: false,
    motoristas: [] as Motorista[],
    placas: [] as Placa[],
    acessos: {
      usuario_externo: false,
      usuario_trigo_produtor: false,
      usuario_local_recebimento_graos: false,
      usuario_recepcao_materia_prima: false,
    } as Acesso,
    ddis: [] as DDI[],
    setores_usa_janelas: {} as Record<string, boolean>,
    setores_operacoes: {} as Record<string, { carregar: boolean; descarregar: boolean }>,
    setores_instrucoes_portaria: {} as Record<string, string>,
    disponibilidade_mes: {} as Record<string, StatusDiaJanela>,
    horarios_disponiveis: [] as HorarioDisponivel[],
    notas_pallet: { ativo: false, perfil: "", notas: [], pagina: 1, tem_mais: false, saldo_total: 0 } as NotasPalletResposta,
  },
  mutations: {
    SET_AUTH_TOKEN(state, token: string) {
      state.auth_token = token;
    },
    SET_USER(state, user: Usuario) {
      state.user = user;
    },
    ADD_ARRAY(state, payload: { nome: string; valores: TArray }) {
      state.arr = {
        ...state.arr,
        [payload.nome]: {
          valores: payload.valores,
          expiraEm: addHours(new Date(), 8),
        },
      };
    },
    SET_IS_SAVING_AGENDAMENTO(state, value) {
      state.isSavingAgendamento = value;
    },
    SET_IS_LOADING_SOLICITA_ACESSO(state, value) {
      state.isLoadingSolicitaAcesso = value;
    },
    SET_MEUS_AGENDAMENTOS(state, valores) {
      state.meus_agendamentos = valores;
    },
    SET_MEUS_AGENDAMENTOS_PAGINACAO(state, valores) {
      state.meus_agendamentos_paginacao = valores;
    },
    SET_FILTROS_MEUS_AGENDAMENTOS(state, valores) {
      state.meus_agendamentos_filtros = valores;
    },
    SET_MOTORISTAS(state, motoristas: Motorista[]) {
      state.motoristas = motoristas;
    },
    SET_PLACAS(state, placas: Placa[]) {
      state.placas = placas;
    },
    SET_ACESSOS(state, acessos: Acesso) {
      state.acessos = {
        ...state.acessos,
        ...acessos,
      };
    },
    SET_DDIS(state, ddis: DDI[]) {
      state.ddis = ddis;
    },
    SET_SETORES_USA_JANELAS(state, setores: Record<string, boolean>) {
      state.setores_usa_janelas = setores;
    },
    SET_SETORES_OPERACOES(
      state,
      setores: Record<string, { carregar: boolean; descarregar: boolean }>
    ) {
      state.setores_operacoes = setores;
    },
    SET_SETORES_INSTRUCOES_PORTARIA(state, setores: Record<string, string>) {
      state.setores_instrucoes_portaria = setores;
    },
    SET_DISPONIBILIDADE_MES(state, dias: Record<string, StatusDiaJanela>) {
      state.disponibilidade_mes = dias;
    },
    SET_HORARIOS_DISPONIVEIS(state, horarios: HorarioDisponivel[]) {
      state.horarios_disponiveis = horarios;
    },
    SET_NOTAS_PALLET(state, notasPallet: NotasPalletResposta) {
      state.notas_pallet = notasPallet;
    },
  },
  actions: {
    async logout(context) {
      context.commit("SET_AUTH_TOKEN", "")
      context.commit("SET_USER", {})
      router.push('/login')
    },
    async login(context, data: ILogin) {
      try {
        const response = await axios.post("/tokens/create", data);
        context.commit("SET_AUTH_TOKEN", response.data.token)
        context.commit("SET_USER", response.data.user)
        return response;
      } catch (error) {
        context.commit("SET_AUTH_TOKEN", "")
        context.commit("SET_USER", {})
        throw error;
      }
    },
    getArray: async (context, payload: string) => {
      if (
        context.state.arr &&
        context.state.arr[payload] &&
        isBefore(
          new Date(),
          typeof context.state.arr[payload].expiraEm === "string"
            ? parseISO(context.state.arr[payload].expiraEm as any)
            : context.state.arr[payload].expiraEm
        )
      ) {
        return;
      }

      const { data } = await axios.get<TArray>(
        `/helpers/lista/${encodeURI(payload)}`
      );

      context.commit("ADD_ARRAY", {
        nome: payload,
        valores: data,
      });
    },
    async getVeiculos(context) {
      const { data } = await axios.get('/report/tipo-veiculo/tiposPortalAgendador')

      const formatado = data.map((i: any) => {
        return {
          name: i.descricao,
          value: i.ukey,
        }
      });

      context.commit("ADD_ARRAY", {
        nome: "array_tipo_veiculo", valores: [
          {
            name: 'Selecione...',
            value: ''
          },
          ...formatado
        ]
      })
    },
    async getSetores(context) {
      const { data } = await axios.get('/report/setores/setoresPortalAgendador')

      const formatado = data.map((i: any) => {
        return {
          name: i.nome,
          value: i.ukey,
        }
      });

      const usaJanelas: Record<string, boolean> = {};
      const operacoes: Record<string, { carregar: boolean; descarregar: boolean }> = {};
      const instrucoesPortaria: Record<string, string> = {};
      data.forEach((i: any) => {
        usaJanelas[i.ukey] = !!i.usa_janelas;
        operacoes[i.ukey] = {
          carregar: i.janela_permite_carregar !== false,
          descarregar: i.janela_permite_descarregar !== false,
        };
        instrucoesPortaria[i.ukey] = i.instrucoes_portaria ?? "";
      });
      context.commit("SET_SETORES_USA_JANELAS", usaJanelas);
      context.commit("SET_SETORES_OPERACOES", operacoes);
      context.commit("SET_SETORES_INSTRUCOES_PORTARIA", instrucoesPortaria);

      context.commit("ADD_ARRAY", {
        nome: "array_setor", valores: [
          {
            name: 'Selecione...',
            value: ''
          },
          ...formatado
        ]
      })
    },
    async getDisponibilidadeMes(context, params: ParametrosDisponibilidadeMes) {
      const query = new URLSearchParams({
        setor_ukey: params.setor_ukey,
        ano: String(params.ano),
        mes: String(params.mes),
        operacao: params.operacao ? String(params.operacao) : "",
      });

      const { data } = await axios.get<DisponibilidadeMes>(
        `/portalagendamentos/janelas/disponibilidade-mes?${query.toString()}`,
        semLoaderGlobal
      );

      context.commit("SET_DISPONIBILIDADE_MES", data?.dias ?? {});
      return data;
    },
    async getHorariosDisponiveis(context, params: ParametrosHorariosDisponiveis) {
      const query = new URLSearchParams({
        setor_ukey: params.setor_ukey,
        data: params.data,
        operacao: params.operacao ? String(params.operacao) : "",
        ignorar_ukey: params.ignorar_ukey ?? "",
      });

      const { data } = await axios.get<HorariosDisponiveisResposta>(
        `/portalagendamentos/janelas/horarios?${query.toString()}`,
        semLoaderGlobal
      );

      context.commit("SET_HORARIOS_DISPONIVEIS", data?.horarios ?? []);
      return data;
    },
    resetLoader(context) {
      context.commit("SET_IS_SAVING_AGENDAMENTO", false);
    },
    async buscaDdis(context) {
      const { data } = await axios.get('/portalagendamentos/ddis')

      const ddisFormatado = data.map((i: DDI) => {
        return {
          name: `${i.nome} +${i.ddi}`,
          value: i.ukey,
          ddi: i.ddi,
        }
      });

      context.commit("SET_DDIS", ddisFormatado)
    },
    async buscarNotasPallet(context, payload: { filtros?: FiltrosNotasPallet; pagina?: number } = {}) {
      const pagina = payload.pagina ?? 1;
      const { data } = await axios.get<NotasPalletResposta>("/portalagendamentos/notas-pallet", {
        params: { ...(payload.filtros ?? {}), pagina },
      });

      if (pagina > 1) {
        context.commit("SET_NOTAS_PALLET", {
          ...data,
          notas: [...context.state.notas_pallet.notas, ...data.notas],
          saldo_total: context.state.notas_pallet.saldo_total,
        });
        return data;
      }

      context.commit("SET_NOTAS_PALLET", data);
      return data;
    },
    async enviarAgendamento(context, payload: DataNewAgendamento) {
      try {
        context.commit("SET_IS_SAVING_AGENDAMENTO", true);
        const { data } = await axios.post("/portalagendamentos/store", payload);

        helpers.mensagemSucesso("Agendamento realizado com sucesso!")
        context.commit("SET_IS_SAVING_AGENDAMENTO", false);
        return data;
      } catch (err) {
        context.commit("SET_IS_SAVING_AGENDAMENTO", false);
        console.log(err)
      }
    },

    async getMeusAgendamentos(context, filtros: any) {
      let filtrosQuery = context.state.meus_agendamentos_filtros
      if (filtros) {
        context.commit("SET_FILTROS_MEUS_AGENDAMENTOS", filtros);
        filtrosQuery = filtros;
      }
      const { data } = await axios.get(`/report/agendamento-transporte/agendamentosDoUsuario?f=${JSON.stringify(filtrosQuery)}`)

      // A API pagina de 10 em 10 (formato do paginator do Laravel);
      // o fallback cobre resposta antiga em formato de array simples.
      context.commit("SET_MEUS_AGENDAMENTOS", data?.data ?? data);
      context.commit("SET_MEUS_AGENDAMENTOS_PAGINACAO", {
        current_page: data?.current_page ?? 1,
        last_page: data?.last_page ?? 1,
        total: data?.total ?? (data?.data ?? data ?? []).length,
      });
    },

    async esqueciSenha(context, usuario: string) {
      try {
        const { data } = await axios.post("/portalagendamentos/esqueci-senha", {
          usuario,
        });

        return data;
      } catch (err) {
        console.log(err);
      }
    },

    async redefinirSenha(context, payload: { token: string; password: string; password_confirmation: string }) {
      try {
        const { data } = await axios.post("/portalagendamentos/redefinir-senha", payload);

        return data;
      } catch (err) {
        console.log(err);
      }
    },

    async solicitaAcesso(context, values: DataSolicitaAcesso) {
      context.commit("SET_IS_LOADING_SOLICITA_ACESSO", true)
      await axios.post("/portalagendamentos/solicita-acesso", values);
      context.commit("SET_IS_LOADING_SOLICITA_ACESSO", false)
    },

    async apagarAgendamento(context, agendamento_ukey: string) {
      try {
        await axios.delete(`/portalagendamentos/delete/${agendamento_ukey}`);

        helpers.mensagemSucesso("Agendamento cancelado com sucesso!")
      } catch (err) {
        console.log(err)
      }
    },

    async getMotoristas(context) {
      const { data } = await axios.get(`/portalagendamentos/motoristas`)

      context.commit("SET_MOTORISTAS", data);
    },

    async saveMotorista(context, motorista: Motorista) {
      const { data } = await axios.post(`/portalagendamentos/motoristas`, motorista)

      context.dispatch("getMotoristas");
      return data;
    },

    async getPlacas(context) {
      const { data } = await axios.get(`/portalagendamentos/placas`)

      context.commit("SET_PLACAS", data);
    },

    async savePlaca(context, placa: Placa) {
      await axios.post(`/portalagendamentos/placas`, placa)

      context.dispatch("getPlacas");
    },
  },
  modules: {
  },
  plugins: [new VuexPersistence().plugin],
}) 
